// Add a boat to PDI: the boat (picked from the season's Overview boats not on PDI yet, or typed in: SAP and
// name) and its model's SAMPLE checklist on one of the season's PDI sheets. The worker puts the boat under
// the model's heading and copies the SAMPLE's sections and items under it (routes/pdi.ts).

import { useMemo, useState } from 'react';
import { pdiModelKey } from '@technohull/shared';
import { useAddPdiBoat, usePdiSamples, type PdiSamplesResponse } from '../features/hooks.js';
import { fold, hasText } from '../lib/search.js';
import { ErrorState, InlineError, LoadingState, Modal, SearchInput } from './ui.js';
import { IconCheck } from './icons.js';

type Boat = PdiSamplesResponse['boats'][number];
/** the SAMPLE that matches a boat's model: the same model, else one whose model the boat's ends with
 *  ("TG-38" → "38"); none when it isn't clear */
function sampleFor(boat: Boat, sheets: PdiSamplesResponse['sheets']): string {
  const all = sheets.flatMap((s) => s.samples.map((x) => ({ value: `${s.sheetId}:${x.rowId}`, key: pdiModelKey(x.model) })));
  const want = pdiModelKey(boat.model);
  if (!want) return '';
  const exact = all.filter((x) => x.key === want);
  if (exact.length === 1) return exact[0]!.value;
  const loose = all.filter((x) => x.key && (want.endsWith(x.key) || want.startsWith(x.key)));
  return loose.length === 1 ? loose[0]!.value : '';
}

export function PdiAddBoat({ onClose }: { onClose: () => void }) {
  const samples = usePdiSamples(true);
  const add = useAddPdiBoat();
  const [q, setQ] = useState('');
  const [sap, setSap] = useState('');
  const [name, setName] = useState('');
  const [pick, setPick] = useState(''); // "<sheetId>:<sampleRowId>"
  const sheets = samples.data?.sheets ?? [];
  const boats = useMemo(() => {
    const query = fold(q.trim());
    const list = samples.data?.boats ?? [];
    return (query ? list.filter((b) => hasText(query, b.sap, b.name, b.model)) : list).slice(0, 40);
  }, [samples.data, q]);
  const choose = (b: Boat) => {
    setSap(b.sap);
    setName(b.name);
    const s = sampleFor(b, sheets);
    if (s) setPick(s);
  };
  const [sheetId, rowId] = pick.split(':');
  const sample = sheets.find((s) => s.sheetId === sheetId)?.samples.find((x) => String(x.rowId) === rowId);
  const sheetLabel = sheets.find((s) => s.sheetId === sheetId)?.label;
  const sapOk = /^\d{6,}$/.test(sap.trim());
  const ready = sapOk && name.trim() && sample && !add.isPending;
  const submit = () => {
    if (!ready || !sample) return;
    add.mutate({ sheetId: sheetId!, sampleId: sample.rowId, sap: sap.trim(), name: name.trim() });
  };

  if (add.isSuccess) {
    return (
      <Modal title="Boat added to PDI" onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
        <p className="pdi-add-done">
          <IconCheck size={16} /> <span><b>{name.trim()}</b> (SAP {sap.trim()}) is on the {add.data.sheet} PDI sheet under {add.data.model}, with the {add.data.rows} checks of its SAMPLE, ready to check.</span>
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Add a boat to PDI"
      onClose={onClose}
      width={620}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!ready} onClick={submit}>{add.isPending ? 'Adding…' : 'Add to PDI'}</button>
        </>
      }
    >
      {samples.isLoading && <LoadingState label="Reading the PDI sheets…" />}
      {samples.error && <ErrorState error={samples.error} onRetry={() => void samples.refetch()} />}
      {samples.data && (
        <>
          <p className="pdi-add-lead">
            The boat goes on its model’s PDI sheet with a copy of the model’s SAMPLE checklist, nothing ticked yet.
          </p>
          {(samples.data.boats.length > 0) && (
            <div className="pdi-add-sec">
              <div className="field-label">From the Overview ({samples.data.boats.length} not on PDI yet)</div>
              <SearchInput value={q} onChange={setQ} placeholder="Search SAP, name, model" width={260} />
              <div className="pdi-add-boats" role="listbox" aria-label="Boats not on PDI yet">
                {boats.map((b) => (
                  <button
                    key={b.sap}
                    type="button"
                    role="option"
                    aria-selected={sap === b.sap}
                    className={`pdi-add-boat${sap === b.sap ? ' active' : ''}`}
                    onClick={() => choose(b)}
                  >
                    <b>{b.name || '-'}</b><span>{[b.sap, b.model].filter(Boolean).join(' · ')}</span>
                  </button>
                ))}
                {!boats.length && <p className="muted pdi-add-none">No boat matches.</p>}
              </div>
            </div>
          )}
          <div className="pdi-add-grid">
            <label>
              <span className="field-label">SAP</span>
              <input className="field" inputMode="numeric" value={sap} onChange={(e) => setSap(e.target.value)} placeholder="9100950" aria-invalid={sap !== '' && !sapOk} />
            </label>
            <label>
              <span className="field-label">Name on the sheet</span>
              <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="The customer or vessel name" />
            </label>
          </div>
          <label className="pdi-add-sec">
            <span className="field-label">Checklist (the model’s SAMPLE)</span>
            <select className="field" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick the model</option>
              {sheets.map((s) => (
                <optgroup key={s.sheetId} label={s.label}>
                  {s.samples.map((x) => <option key={x.rowId} value={`${s.sheetId}:${x.rowId}`}>{x.model} ({x.rows - 1} checks)</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          {sample && sheetLabel && <p className="muted pdi-add-where">Goes on the {sheetLabel} sheet, under {sample.model}.</p>}
          {!sheets.some((s) => s.samples.length) && <p className="muted">No SAMPLE checklist was found on this season’s PDI sheets.</p>}
          {add.isError && <InlineError error={add.error} onDismiss={() => add.reset()} />}
        </>
      )}
    </Modal>
  );
}
